import { Button, useToast } from '@sl/react-datafabric-components';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { useRef, useState, type FC } from 'react';
import {
  useCreateBillingPortalSessionMutation,
  useReactivateSubscriptionMutation,
  useRetryPaymentMutation,
  useUnsubscribeMutation,
} from '../../../../common/redux/services/subscriptions';
import getErrorMessage from '../../../../common/utils/get-error-message';
import { CancelSubscriptionDialog } from './cancel-subscription-dialog';

const itemRenderer = (
  item: Omit<MenuItem, 'data'> & { data?: { color?: string } },
  options: MenuItemOptions
) => (
  <div className='p-menuitem-content' onClick={(e) => options.onClick(e)}>
    <a className='flex align-items-center p-menuitem-link'>
      <span className={`${item.icon}  ${item.data?.color ?? 'text-700'}`} />
      <span className={`mx-2 ${item.data?.color ?? 'text-700'}`}>
        {item.label}
      </span>
    </a>
  </div>
);

interface SubscriptionOptionsMenuProps {
  rowData: any; // Pass rowData from DataGrid
}

export const SubscriptionOptionsMenu: FC<SubscriptionOptionsMenuProps> = ({
  rowData,
}) => {
  const menu = useRef<Menu>(null);
  const { showToast } = useToast();

  const [createBillingPortalSession, { isLoading: isCreatingSession }] =
    useCreateBillingPortalSessionMutation();
  const [isRetryingPayment, setIsRetryingPayment] = useState(false);
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false);

  const [retryPayment] = useRetryPaymentMutation();
  const [reactivateSubscription] = useReactivateSubscriptionMutation();
  const [unsubscribeMutation, { isLoading: isCancellingSubscription }] =
    useUnsubscribeMutation();

  const onUpdate = async () => {
    if (!rowData.providerMetadata.customerId) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: 'Customer ID not found for this subscription.',
        },
        'top-right'
      );
      return;
    }
    try {
      const response = await createBillingPortalSession({
        customerId: rowData.providerMetadata.customerId,
      }).unwrap();
      if (response.url) {
        window.location.href = response.url; // Redirect to Stripe's billing portal
      } else {
        showToast(
          {
            severity: 'error',
            summary: 'Error',
            detail: 'Could not retrieve billing portal URL.',
          },
          'top-right'
        );
      }
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to open billing portal.',
        },
        'top-right'
      );
    }
  };

  const onRetry = async () => {
    setIsRetryingPayment(true);
    try {
      const response = await retryPayment(rowData.id).unwrap();
      // Assuming the status might change, you might want to refetch data or rely on optimistic updates
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail: 'Retry successful!',
        },
        'top-right'
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to retry payment.',
        },
        'top-right'
      );
    } finally {
      setIsRetryingPayment(false);
    }
  };

  const onReactivate = async () => {
    try {
      const response = await reactivateSubscription(rowData.id).unwrap();
      // Assuming the status might change, you might want to refetch data or rely on optimistic updates
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail: 'Reactivation successful!',
        },
        'top-right'
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail:
            getErrorMessage(error) || 'Failed to reactivate subscription.',
        },
        'top-right'
      );
    }
  };

  const onDelete = () => {
    setIsCancelDialogVisible(true);
  };

  const onConfirmCancel = async ({
    reason,
    comment,
  }: {
    reason: string;
    comment?: string;
  }) => {
    try {
      await unsubscribeMutation({
        id: rowData.id,
        reason,
        comment,
      }).unwrap();
      setIsCancelDialogVisible(false);
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail:
            'Auto-renewal has been canceled. Your subscription will remain active until the end of the cycle.',
        },
        'top-right'
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to cancel auto-renewal.',
        },
        'bottom-right'
      );
    }
  };

  const normalizedStatus = String(rowData?.status || '')
    .trim()
    .toLowerCase();
  const canManageRenewal = ['active', 'paymentfailed'].includes(normalizedStatus);
  const hasScheduledCancellation = canManageRenewal && Boolean(rowData?.endDate);

  const actionItems: MenuItem[] = [
    ...[
      {
        label: 'Update billing details',
        icon: 'pi pi-credit-card',
        template: itemRenderer,
        command: onUpdate,
      },
      ...(canManageRenewal
        ? [
            {
              label: hasScheduledCancellation
                ? 'Reactivate'
                : 'Cancel auto-renewal',
              icon: `pi pi-${hasScheduledCancellation ? 'refresh' : 'ban'}`,
              template: itemRenderer,
              command: hasScheduledCancellation ? onReactivate : onDelete,
              data: {
                color: hasScheduledCancellation
                  ? 'text-green-500'
                  : 'text-red-500',
              },
            },
          ]
        : []),
      ...(rowData.status === 'paymentFailed'
        ? [
            {
              label: 'Retry Payment',
              icon: isRetryingPayment
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-replay',
              template: itemRenderer,
              command: onRetry,
              data: { color: 'text-orange-500' },
              disabled: isRetryingPayment,
            },
          ]
        : []),
    ],
  ];

  return (
    <>
      <Menu
        model={actionItems}
        popup
        ref={menu}
        id='popup_menu_right'
        popupAlignment='right'
      />
      <div className='flex flex-nowrap'>
        <Button
          severity='secondary'
          outlined
          rounded
          icon='pi pi-ellipsis-v'
          className='border-300'
          onClick={(event) => menu.current?.toggle(event)}
          loading={isCreatingSession} // Keep existing loading for other actions if needed
          aria-controls='popup_menu_right'
          aria-haspopup
          disabled={rowData?.plan?.trial}
        />
      </div>
      <CancelSubscriptionDialog
        visible={isCancelDialogVisible}
        planName={String(rowData?.plan?.name || '')}
        loading={isCancellingSubscription}
        onHide={() => {
          if (!isCancellingSubscription) {
            setIsCancelDialogVisible(false);
          }
        }}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};
